import React, { useState } from "react";
import {
  EuiButton,
  EuiFieldText,
  EuiForm,
  EuiFormRow,
  EuiFilePicker,
  EuiSpacer,
  EuiSuperSelect,
  EuiDatePicker,
  EuiTextArea,
  htmlIdGenerator,
  EuiRadioGroup,
} from "@elastic/eui";

import { useHistory } from "react-router-dom";
import { v4 } from "uuid";
import { useForm } from "react-hook-form";
import { Moment } from "moment";

import { applicationClass, formClass } from "../styles";
import { db, storage } from "../firebase/firebase";
import { genderOptions, innovStateOptions } from "../common/Options";
import { NTBCHero } from "./NTBCHero";

const idPrefix = htmlIdGenerator()();

const NTBCApplicationForm: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const [gender, setGender] = useState<string>("");
  const [eduErrors, setEduError] = useState<boolean>(false);
  const [dateOfBirth, setdateOfBirth] = useState<Moment>();
  const [dobErrors, setDobErrors] = useState<boolean>(false);
  const [innovationState, setInnovationState] = useState<string>("");
  const [innovationStage, setInnovationStage] = useState<string>("");

  const radios = [
    {
      id: `${idPrefix}0`,
      label: "Yes",
    },
    {
      id: `${idPrefix}1`,
      label: "No",
    },
  ];

  const [radioIdSelected, setRadioIdSelected] = useState(`${idPrefix}0`);
  const onRadioChange = (optionId: any) => {
    setRadioIdSelected(optionId);
  };

  const [files, setFiles] = useState<any>(null);
  const onChange = (fs: any) => {
    setFiles(fs);
  };

  const { register, handleSubmit, errors } = useForm();

  const onFormSubmit = async (data: any) => {
    console.log(files);

    if (!gender) {
      setEduError(true);
      return;
    }

    if (!dateOfBirth) {
      setDobErrors(true);
      return;
    } else {
      setLoading(true);
      const id = v4();

      if (files) {
        await storage()
          .ref(`/nii-applications/${id}/${files[0].name}`)
          .put(files[0])
          .then((x) =>
            x.ref.getDownloadURL().then(async (url) => {
              await db()
                .collection("innovations")
                .doc(id)
                .set({
                  id,
                  ...data,
                  coverImage: url,
                  assessmentTechnicalComplete: false,
                  assessmentIPComplete: false,
                  assessmentMarketComplete: false,
                  businessPlanningComplete: false,
                  productLaunched: false,
                  promoterPhotoUrl: "",
                  innovationSector: "",
                  innovationMedia: "",
                  innovationState,
                  innovationStage,
                  innovationStatus: "Submitted",
                  publicView: false,
                  backers: [],
                  dateUpdated: db.Timestamp.now(),
                  document: {},
                  comments: [],
                  protectionStatus: radios.map(
                    (x) => x.id === radioIdSelected
                  )[0],
                  dateOfBirth: db.Timestamp.fromDate(dateOfBirth.toDate()),
                  timestamp: db.Timestamp.now(),
                });
            })
          );
      } else {
      }
    }

    localStorage.setItem("recipient", data.email);
    localStorage.setItem("recipeintsName", data.innovatorName);

    history.push("/submitted");
  };

  const handleChange = (date: any) => {
    setdateOfBirth(date);
  };

  return (
    <div>
      <div></div>
      <div className={applicationClass}>
        <EuiForm
          component="form"
          className={formClass}
          onSubmit={handleSubmit(onFormSubmit)}
          isInvalid={dobErrors}
        >
          <EuiFormRow
            fullWidth
            label="Name of Innovator"
            helpText="If you are in a group, add the name of the team leader."
          >
            <EuiFieldText
              name="promoter"
              inputRef={register({ required: true })}
              isInvalid={errors.innovatorName && true}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow label="Date of Birth" isInvalid={false} fullWidth>
            <EuiDatePicker
              isInvalid={dobErrors}
              selected={dateOfBirth}
              placeholder="Enter date of birth"
              onChange={handleChange}
              fullWidth
            />
          </EuiFormRow>
          <EuiFormRow label="Gender" isInvalid={eduErrors} fullWidth>
            <EuiSuperSelect
              options={genderOptions}
              valueOfSelected={gender}
              fullWidth
              isInvalid={eduErrors}
              onChange={(value: any) => setGender(value)}
            />
          </EuiFormRow>
          <EuiFormRow
            label="NRC Number"
            helpText="Enter your NRC, i.e., 123456/78/9."
            fullWidth
          >
            <EuiFieldText
              name="nrcNumber"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>
          <EuiFormRow
            label="Contact Number"
            helpText="Your primary contact number."
            fullWidth
          >
            <EuiFieldText
              name="promoterPhone"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow
            label="Home Address"
            helpText="Please include your province"
            fullWidth
          >
            <EuiFieldText
              name="promoterAddress"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow label="Email address" fullWidth>
            <EuiFieldText
              name="promoterEmail"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow
            label="Title of Innovation"
            helpText="Maximum of 50 words."
            fullWidth
          >
            <EuiFieldText
              name="innovationName"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow
            label="Brief Innovation Description"
            helpText="Maximum of 200 words."
            fullWidth
          >
            <EuiTextArea
              name="innovationDescription"
              placeholder="Give a brief description on what your innovation is and what problem is being solved or process being improved"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow
            label="Target Market"
            helpText="Maximum of 200 words."
            fullWidth
          >
            <EuiTextArea
              name="targetMarket"
              placeholder="Explain how your innovation will generate income for you or others or how it can assist in improving operations of existing businesses, specify your target market/potential offtakers"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>
          <EuiFormRow
            label="Market Need"
            helpText="Maximum of 200 words."
            fullWidth
          >
            <EuiTextArea
              name="marketNeed"
              placeholder="Explain how your innovation will generate income for you or others or how it can assist in improving operations of existing businesses, specify your target market/potential offtakers"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow
            label="Raw Materials"
            helpText="Maximum of 200 words."
            fullWidth
          >
            <EuiTextArea
              name="rawMaterials"
              placeholder="Explain how your innovation is designed and what prototype exists if any"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow
            label="Economic Benefits"
            helpText="Maximum of 200 words."
            fullWidth
          >
            <EuiTextArea
              name="economicBenefits"
              placeholder="Explain how your innovation can improve the lives of others"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow
            label="Innovation Benefits"
            helpText="Maximum of 200 words."
            fullWidth
          >
            <EuiTextArea
              name="socialImpact"
              placeholder="Explain how your innovation can improve the lives of others"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow>
            <EuiRadioGroup
              options={radios}
              idSelected={radioIdSelected}
              onChange={(id) => onRadioChange(id)}
              name="protectionStatus"
              legend={{
                children: <span>Is your innovation protected?</span>,
              }}
            />
          </EuiFormRow>

          <EuiFormRow
            label="Supporting Image"
            helpText="Add an image to support your innovation"
            fullWidth
          >
            <EuiFilePicker
              fullWidth
              accept="image/x-png,image/gif,image/jpeg"
              onChange={(files) => {
                onChange(files);
              }}
            />
          </EuiFormRow>

          <EuiFormRow
            label="Supporting documents"
            helpText="Add any document to support your innovation."
            fullWidth
          >
            <EuiFilePicker
              fullWidth
              onChange={(files) => {
                onChange(files);
              }}
            />
          </EuiFormRow>

          <EuiFormRow label="Innovation State" isInvalid={eduErrors} fullWidth>
            <EuiSuperSelect
              options={innovStateOptions}
              valueOfSelected={innovationState}
              fullWidth
              isInvalid={eduErrors}
              onChange={(value: any) => setInnovationState(value)}
            />
          </EuiFormRow>
          <EuiFormRow label="Innovation Stage" isInvalid={eduErrors} fullWidth>
            <EuiSuperSelect
              options={innovStateOptions}
              valueOfSelected={innovationStage}
              fullWidth
              isInvalid={eduErrors}
              onChange={(value: any) => setInnovationStage(value)}
            />
          </EuiFormRow>

          <EuiFormRow
            label="Company Name"
            helpText="Add a company name, if any."
            fullWidth
          >
            <EuiFieldText
              name="promoterCompany"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow
            label="Company Address"
            helpText="Add address of your company if your have one"
            fullWidth
          >
            <EuiFieldText
              name="promoterCompanyAddress"
              inputRef={register({ required: true })}
              fullWidth
            />
          </EuiFormRow>

          <EuiSpacer />

          <EuiSpacer />

          <EuiButton fill type="submit" isLoading={loading}>
            Submit Innovation
          </EuiButton>
        </EuiForm>
        <EuiSpacer />
        <EuiSpacer />
      </div>
    </div>
  );
};

export const NTBCApplication: React.FunctionComponent = () => {
  return (
    <div>
      <NTBCHero>
        <NTBCApplicationForm />
      </NTBCHero>
    </div>
  );
};
